import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  exportAttendanceAuditLogs,
  exportAttendanceCrewLog,
  getAttendanceCrewLog,
  listAttendanceAuditLogBases,
  listAttendanceAuditLogShifts,
  listAttendanceAuditLogs,
} from '../../../api/scheduler/functions';
import { SchedulerApiConfiguration } from '../../../api/scheduler/scheduler-api-configuration';
import {
  AttendanceLogBase,
  AttendanceLogPage,
  AttendanceLogQuery,
  AttendanceShiftOption,
  CrewLogQuery,
  CrewLogResult,
} from '../models';
import { toAttendanceShiftOption } from '../utils/attendance-shift-filter.util';
import { toCrewLogExportParams, toCrewLogParams, toCrewLogResult } from '../utils/crew-log.mapper';
import {
  toAttendanceLogBase,
  toAttendanceLogPage,
  toExportParams,
  toListParams,
} from '../utils/attendance-log.mapper';

/** The browser's IANA zone, which the API returns, calculates delays in, and exports clock-in and clock-out times in. */
function currentTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

@Injectable({ providedIn: 'root' })
export class AttendanceLogApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SchedulerApiConfiguration);

  list(query: AttendanceLogQuery): Observable<AttendanceLogPage> {
    return listAttendanceAuditLogs(this.http, this.config.rootUrl, {
      ...toListParams(query),
      timeZone: currentTimeZone(),
    }).pipe(map((res) => toAttendanceLogPage(res.body)));
  }

  listBases(): Observable<AttendanceLogBase[]> {
    return listAttendanceAuditLogBases(this.http, this.config.rootUrl).pipe(
      map((res) => (res.body.data ?? []).map(toAttendanceLogBase))
    );
  }

  listShifts(): Observable<AttendanceShiftOption[]> {
    return listAttendanceAuditLogShifts(this.http, this.config.rootUrl).pipe(
      map((res) => (res.body.data ?? []).map(toAttendanceShiftOption))
    );
  }

  /** Every row matching the query (ignoring paging), as an Excel workbook. */
  export(query: AttendanceLogQuery): Observable<Blob> {
    return exportAttendanceAuditLogs(this.http, this.config.rootUrl, {
      ...toExportParams(query),
      timeZone: currentTimeZone(),
    }).pipe(map((res) => res.body));
  }

  getCrewLog(query: CrewLogQuery): Observable<CrewLogResult> {
    return getAttendanceCrewLog(this.http, this.config.rootUrl, {
      ...toCrewLogParams(query),
      timeZone: currentTimeZone(),
    }).pipe(map((res) => toCrewLogResult(res.body)));
  }

  /** Every row matching the query (ignoring paging), as an Excel workbook. */
  exportCrewLog(query: CrewLogQuery): Observable<Blob> {
    return exportAttendanceCrewLog(this.http, this.config.rootUrl, {
      ...toCrewLogExportParams(query),
      timeZone: currentTimeZone(),
    }).pipe(map((res) => res.body));
  }
}
